import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import { useBatchStore } from '../../stateManagers/stores/batchStore';
import BatchFilterControl from '../../components/BatchSelector';
// Adjust this path to wherever BatchFilterControl actually lives in your project.

/* ─── Design Tokens ─────────────────────────────────────────────────── */
const C = {
  navy: '#0e225d', navyMid: '#1a3370',
  gold: '#fbb21f', goldDim: 'rgba(251,178,31,0.14)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', bg: '#F5F7FA', border: '#e5e7eb',
  muted: '#9ca3af', sub: '#6b7280', body: '#1f2937',
  green: '#10B981', greenDim: 'rgba(16,185,129,0.1)', greenBorder: 'rgba(16,185,129,0.3)',
  red: '#DC2626', redDim: 'rgba(220,38,38,0.1)', redBorder: 'rgba(220,38,38,0.3)',
  amber: '#fbb21f', amberDim: 'rgba(251,178,31,0.12)', amberBorder: 'rgba(251,178,31,0.35)',
  blue: '#3b82f6', blueDim: 'rgba(59,130,246,0.1)', blueBorder: 'rgba(59,130,246,0.3)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
type AttendanceStatus = 'present' | 'absent' | 'late';
type ActiveTab = 'mark' | 'reports';

interface Student {
  student_id: string;
  name: string;
  mobileNumber: string | null;
  email: string | null;
  profilePicture: { cdn_url?: string; dev_url?: string } | null;
  date_of_admission: string;
}

interface AttendanceRecord {
  student_id: string;
  status: AttendanceStatus;
  remarks?: string;
}

interface ExistingAttendance {
  attendance_id: string;
  topic: string;
  attendance_records: Array<{ student_id: string; status: string; remarks?: string }>;
}

interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  is_active: boolean;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
}

/* ─── Report Types ───────────────────────────────────────────────────── */
interface ReportStudentRow {
  student_id: string;
  student_name: string;
  student_mobile: string | null;
  student_email: string | null;
  profile_picture: string | null;
  total_classes: number;
  attended_classes: number;
  present: number;
  absent: number;
  late: number;
  excused: number;
  attendance_percentage: number;
  records: ReportRecord[];
}

interface ReportRecord {
  attendance_id: string;
  date: string;
  topic: string | null;
  status: string;
  remarks: string | null;
  marked_by: string | null;
  marked_by_name: string | null;
  marked_at: string | null;
}

interface BatchAttendanceSummary {
  attendance_id: string;
  batch_id: string;
  attendance_date: string;
  topic: string | null;
  total_students: number;
  present_count: number;
  absent_count: number;
  late_count: number;
  excused_count: number;
}

/* ─── Status Config ──────────────────────────────────────────────────── */
const STATUS_CONFIG: Record<AttendanceStatus, {
  label: string; color: string; dim: string; borderColor: string; icon: string;
}> = {
  present: { label: 'Present', color: C.green,  dim: C.greenDim,  borderColor: C.greenBorder, icon: 'pi-check-circle' },
  absent:  { label: 'Absent',  color: C.red,    dim: C.redDim,    borderColor: C.redBorder,   icon: 'pi-times-circle' },
  late:    { label: 'Late',    color: C.amber,  dim: C.amberDim,  borderColor: C.amberBorder, icon: 'pi-clock' },
};

/* ─── Helpers ────────────────────────────────────────────────────────── */
const formatDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const displayDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const getInitial = (name: string) => name.charAt(0).toUpperCase();

const getPctColor = (pct: number) => {
  if (pct >= 75) return C.green;
  if (pct >= 50) return C.amber;
  return C.red;
};

const getPctDim = (pct: number) => {
  if (pct >= 75) return C.greenDim;
  if (pct >= 50) return C.amberDim;
  return C.redDim;
};

const getPctBorder = (pct: number) => {
  if (pct >= 75) return C.greenBorder;
  if (pct >= 50) return C.amberBorder;
  return C.redBorder;
};

/* ─── PDF Generation ─────────────────────────────────────────────────── */
const HCI_LOGO = 'https://media.hciindia.in/email/new-hci-logo.png';

const buildStudentSummaryPdf = (params: {
  rows: ReportStudentRow[];
  batch: Batch;
  summary: { total_students: number; total_classes: number; average_attendance: number } | null;
  startDate: string;
  endDate: string;
  instituteName: string;
  branchName: string;
}) => {
  const { rows, batch, summary, startDate, endDate, instituteName, branchName } = params;
  const dateRange = startDate
    ? `${displayDate(startDate)} – ${displayDate(endDate)}`
    : `All dates up to ${displayDate(endDate)}`;

  const avgColor = summary ? getPctColor(summary.average_attendance) : C.green;

  const studentRows = rows.map((r, i) => {
    const pctColor = getPctColor(r.attendance_percentage);
    const statusBg = r.attendance_percentage >= 75 ? '#bbf7d0' : r.attendance_percentage >= 50 ? '#fde68a' : '#fecaca';
    const statusTextColor = r.attendance_percentage >= 75 ? '#14532d' : r.attendance_percentage >= 50 ? '#713f12' : '#7f1d1d';
    const statusBorderColor = r.attendance_percentage >= 75 ? '#16a34a' : r.attendance_percentage >= 50 ? '#ca8a04' : '#dc2626';
    return `
      <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;font-size:11px;font-weight:700;color:#0e225d;">${i + 1}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;">
          <div style="font-size:11px;font-weight:700;color:#0e225d;">${r.student_name}</div>
          ${r.student_mobile ? `<div style="font-size:10px;color:#6b7280;margin-top:2px;">${r.student_mobile}</div>` : ''}
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;font-size:11px;color:#0e225d;font-weight:700;">${r.total_classes}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(16,185,129,0.1);color:#10B981;border:1px solid rgba(16,185,129,0.3);">${r.present}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(220,38,38,0.1);color:#DC2626;border:1px solid rgba(220,38,38,0.3);">${r.absent}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(251,178,31,0.12);color:#fbb21f;border:1px solid rgba(251,178,31,0.35);">${r.late}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:800;background:${statusBg};color:${statusTextColor};border:1.5px solid ${statusBorderColor};">${r.attendance_percentage}%</span>
        </td>
      </tr>`;
  }).join('');

  return `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #000; background: white; }
      @page { size: A4; margin: 18mm 14mm 18mm 14mm; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style>

    <!-- Header -->
    <div style="display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;border-bottom:3px solid #0e225d;margin-bottom:14px;">
      <img src="${HCI_LOGO}" alt="Logo" style="width:130px;height:50px;object-fit:contain;" />
      <div style="text-align:center;flex:1;padding:0 16px;">
        <h1 style="font-size:20px;font-weight:800;color:#0e225d;letter-spacing:-0.3px;line-height:1.2;">${instituteName}</h1>
        ${branchName ? `<div style="font-size:12px;font-weight:700;color:#1f2937;margin-top:3px;">${branchName}</div>` : ''}
      </div>
      <div style="text-align:right;font-size:11px;color:#1f2937;line-height:2;min-width:140px;">
        <div><strong style="color:#0e225d;">Generated:</strong> ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
        <div><strong style="color:#0e225d;">Time:</strong> ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>

    <!-- Banner -->
    <div style="background:#0e225d;color:#fff;padding:9px 16px;border-radius:6px;display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
      <span style="font-size:13px;font-weight:800;letter-spacing:1px;">ATTENDANCE REPORT — BY STUDENT</span>
      <span style="background:#fbb21f;color:#0e225d;padding:3px 14px;border-radius:20px;font-size:11px;font-weight:800;letter-spacing:0.5px;">${batch.batch_name}</span>
    </div>

    <!-- Info Grid -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;">
      <div style="border:1.5px solid #94a3b8;border-radius:8px;padding:12px 14px;">
        <h4 style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;margin-bottom:9px;border-bottom:1px solid #94a3b8;padding-bottom:6px;">Batch Information</h4>
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Batch Name</span>
          <span style="font-weight:700;color:#0e225d;">${batch.batch_name}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Timing</span>
          <span style="font-weight:700;color:#0e225d;">${batch.batch_time}</span>
        </div>
        ${branchName ? `<div style="display:flex;justify-content:space-between;font-size:11px;gap:8px;"><span style="color:#374151;font-weight:500;">Branch</span><span style="font-weight:700;color:#0e225d;">${branchName}</span></div>` : ''}
      </div>
      <div style="border:1.5px solid #94a3b8;border-radius:8px;padding:12px 14px;">
        <h4 style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;margin-bottom:9px;border-bottom:1px solid #94a3b8;padding-bottom:6px;">Report Period</h4>
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Date Range</span>
          <span style="font-weight:700;color:#0e225d;">${dateRange}</span>
        </div>
        ${summary ? `
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Total Students</span>
          <span style="font-weight:700;color:#0e225d;">${summary.total_students}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:11px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Classes Held</span>
          <span style="font-weight:700;color:#0e225d;">${summary.total_classes}</span>
        </div>` : ''}
      </div>
    </div>

    ${summary ? `
    <!-- Summary Stats -->
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
      <div style="width:3px;height:14px;background:#fbb21f;border-radius:3px;"></div>
      <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;">Batch Summary</span>
    </div>
    <div style="border:1.5px solid #94a3b8;border-radius:8px;overflow:hidden;margin-bottom:16px;">
      <div style="display:flex;border-bottom:1px solid #e5e7eb;">
        <div style="flex:1;padding:12px 16px;border-right:1px solid #e5e7eb;text-align:center;">
          <div style="font-size:10px;color:#6b7280;font-weight:600;margin-bottom:4px;">Total Students</div>
          <div style="font-size:18px;font-weight:800;color:#0e225d;">${summary.total_students}</div>
        </div>
        <div style="flex:1;padding:12px 16px;border-right:1px solid #e5e7eb;text-align:center;">
          <div style="font-size:10px;color:#6b7280;font-weight:600;margin-bottom:4px;">Classes Held</div>
          <div style="font-size:18px;font-weight:800;color:#3b82f6;">${summary.total_classes}</div>
        </div>
        <div style="flex:1;padding:12px 16px;text-align:center;">
          <div style="font-size:10px;color:#6b7280;font-weight:600;margin-bottom:4px;">Avg Attendance</div>
          <div style="font-size:18px;font-weight:800;color:${avgColor};">${summary.average_attendance}%</div>
        </div>
      </div>
    </div>` : ''}

    <!-- Student Table -->
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
      <div style="width:3px;height:14px;background:#fbb21f;border-radius:3px;"></div>
      <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;">Student-wise Attendance</span>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead>
        <tr style="background:#dde4f0;">
          <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">#</th>
          <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Student</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Classes</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Present</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Absent</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Late</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;">Attendance %</th>
        </tr>
      </thead>
      <tbody>${studentRows}</tbody>
    </table>

    <!-- Footer -->
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:24px;padding-top:16px;border-top:1.5px dashed #64748b;">
      <div style="font-size:10px;color:#374151;line-height:1.8;max-width:55%;">
        <strong style="color:#0e225d;">Note:</strong> This is a computer-generated attendance report.<br/>
        For queries, contact the institute administration.
      </div>
      <div style="text-align:center;">
        <div style="height:40px;"></div>
        <div style="width:150px;border-top:1.5px solid #0e225d;padding-top:6px;font-size:10px;color:#374151;font-weight:600;">Authorised Signatory</div>
        <div style="font-size:10px;color:#0e225d;font-weight:700;margin-top:3px;">${branchName || instituteName}</div>
      </div>
    </div>
    <div style="text-align:center;margin-top:18px;padding-top:14px;border-top:1px solid #94a3b8;">
      <div style="font-size:9px;color:#374151;">Generated on ${new Date().toLocaleString('en-IN')}</div>
    </div>
  `;
};

const buildDateWisePdf = (params: {
  rows: BatchAttendanceSummary[];
  batch: Batch;
  startDate: string;
  endDate: string;
  instituteName: string;
  branchName: string;
}) => {
  const { rows, batch, startDate, endDate, instituteName, branchName } = params;
  const dateRange = startDate
    ? `${displayDate(startDate)} – ${displayDate(endDate)}`
    : `All dates up to ${displayDate(endDate)}`;

  const sessionRows = rows.map((s, i) => {
    const total = s.total_students || 1;
    const presentPct = Math.round((s.present_count / total) * 100);
    const pctColor = getPctColor(presentPct);
    const statusBg = presentPct >= 75 ? '#bbf7d0' : presentPct >= 50 ? '#fde68a' : '#fecaca';
    const statusTextColor = presentPct >= 75 ? '#14532d' : presentPct >= 50 ? '#713f12' : '#7f1d1d';
    const statusBorderColor = presentPct >= 75 ? '#16a34a' : presentPct >= 50 ? '#ca8a04' : '#dc2626';
    return `
      <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;font-size:11px;font-weight:700;color:#0e225d;">${displayDate(s.attendance_date)}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;font-size:11px;color:#374151;">${s.topic || '—'}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;font-size:11px;font-weight:700;color:#0e225d;">${s.total_students}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(16,185,129,0.1);color:#10B981;border:1px solid rgba(16,185,129,0.3);">${s.present_count}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(220,38,38,0.1);color:#DC2626;border:1px solid rgba(220,38,38,0.3);">${s.absent_count}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;border-right:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;background:rgba(251,178,31,0.12);color:#fbb21f;border:1px solid rgba(251,178,31,0.35);">${s.late_count}</span>
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">
          <span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:800;background:${statusBg};color:${statusTextColor};border:1.5px solid ${statusBorderColor};">${presentPct}%</span>
        </td>
      </tr>`;
  }).join('');

  return `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #000; background: white; }
      @page { size: A4; margin: 18mm 14mm 18mm 14mm; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style>

    <!-- Header -->
    <div style="display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;border-bottom:3px solid #0e225d;margin-bottom:14px;">
      <img src="${HCI_LOGO}" alt="Logo" style="width:130px;height:50px;object-fit:contain;" />
      <div style="text-align:center;flex:1;padding:0 16px;">
        <h1 style="font-size:20px;font-weight:800;color:#0e225d;letter-spacing:-0.3px;line-height:1.2;">${instituteName}</h1>
        ${branchName ? `<div style="font-size:12px;font-weight:700;color:#1f2937;margin-top:3px;">${branchName}</div>` : ''}
      </div>
      <div style="text-align:right;font-size:11px;color:#1f2937;line-height:2;min-width:140px;">
        <div><strong style="color:#0e225d;">Generated:</strong> ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
        <div><strong style="color:#0e225d;">Time:</strong> ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>

    <!-- Banner -->
    <div style="background:#0e225d;color:#fff;padding:9px 16px;border-radius:6px;display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
      <span style="font-size:13px;font-weight:800;letter-spacing:1px;">ATTENDANCE REPORT — BY DATE</span>
      <span style="background:#fbb21f;color:#0e225d;padding:3px 14px;border-radius:20px;font-size:11px;font-weight:800;letter-spacing:0.5px;">${batch.batch_name}</span>
    </div>

    <!-- Info Grid -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;">
      <div style="border:1.5px solid #94a3b8;border-radius:8px;padding:12px 14px;">
        <h4 style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;margin-bottom:9px;border-bottom:1px solid #94a3b8;padding-bottom:6px;">Batch Information</h4>
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Batch Name</span>
          <span style="font-weight:700;color:#0e225d;">${batch.batch_name}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:11px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Timing</span>
          <span style="font-weight:700;color:#0e225d;">${batch.batch_time}</span>
        </div>
      </div>
      <div style="border:1.5px solid #94a3b8;border-radius:8px;padding:12px 14px;">
        <h4 style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;margin-bottom:9px;border-bottom:1px solid #94a3b8;padding-bottom:6px;">Report Period</h4>
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:5px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Date Range</span>
          <span style="font-weight:700;color:#0e225d;">${dateRange}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:11px;gap:8px;">
          <span style="color:#374151;font-weight:500;">Sessions</span>
          <span style="font-weight:700;color:#0e225d;">${rows.length}</span>
        </div>
      </div>
    </div>

    <!-- Sessions Table -->
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
      <div style="width:3px;height:14px;background:#fbb21f;border-radius:3px;"></div>
      <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.7px;color:#0e225d;">Session-wise Attendance</span>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead>
        <tr style="background:#dde4f0;">
          <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Date</th>
          <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Topic</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Total</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Present</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Absent</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;border-right:1px solid #94a3b8;">Late</th>
          <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;color:#0e225d;border-bottom:2px solid #0e225d;">Present %</th>
        </tr>
      </thead>
      <tbody>${sessionRows}</tbody>
    </table>

    <!-- Footer -->
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:24px;padding-top:16px;border-top:1.5px dashed #64748b;">
      <div style="font-size:10px;color:#374151;line-height:1.8;max-width:55%;">
        <strong style="color:#0e225d;">Note:</strong> This is a computer-generated attendance report.<br/>
        For queries, contact the institute administration.
      </div>
      <div style="text-align:center;">
        <div style="height:40px;"></div>
        <div style="width:150px;border-top:1.5px solid #0e225d;padding-top:6px;font-size:10px;color:#374151;font-weight:600;">Authorised Signatory</div>
        <div style="font-size:10px;color:#0e225d;font-weight:700;margin-top:3px;">${branchName || instituteName}</div>
      </div>
    </div>
    <div style="text-align:center;margin-top:18px;padding-top:14px;border-top:1px solid #94a3b8;">
      <div style="font-size:9px;color:#374151;">Generated on ${new Date().toLocaleString('en-IN')}</div>
    </div>
  `;
};

const openPrintWindow = (htmlBody: string, title: string) => {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title></head><body>${htmlBody}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 350);
};

/* ─── Sub-components ─────────────────────────────────────────────────── */

const StatusToggle = ({
  status, onChange,
}: { status: AttendanceStatus; onChange: (s: AttendanceStatus) => void }) => (
  <div style={{ display: 'flex', gap: '6px' }}>
    {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map(s => {
      const cfg = STATUS_CONFIG[s];
      const active = status === s;
      return (
        <button
          key={s}
          onClick={() => onChange(s)}
          style={{
            flex: 1, padding: '5px 0', fontSize: '0.7rem', fontWeight: '700',
            borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s ease',
            border: `1.5px solid ${active ? cfg.color : C.border}`,
            background: active ? cfg.dim : C.white,
            color: active ? cfg.color : C.muted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
          }}
        >
          <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.68rem' }} />
          {cfg.label}
        </button>
      );
    })}
  </div>
);

const StudentRow = ({
  student, record, onStatusChange, onRemarksChange, index, viewOnly = false, isChanged = false,
}: {
  student: Student;
  record: AttendanceRecord;
  onStatusChange: (id: string, s: AttendanceStatus) => void;
  onRemarksChange: (id: string, r: string) => void;
  index: number;
  viewOnly?: boolean;
  isChanged?: boolean;
}) => {
  const [showRemarks, setShowRemarks] = useState(false);
  const cfg = STATUS_CONFIG[record.status];

  return (
    <div style={{
      background: isChanged ? 'rgba(251,178,31,0.04)' : C.white,
      border: `1px solid ${isChanged ? C.amberBorder : C.border}`,
      borderRadius: '10px', padding: '12px 14px',
      borderLeft: `3px solid ${isChanged ? C.amber : cfg.color}`,
      transition: 'box-shadow 0.15s ease',
    }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 10px rgba(14,34,93,0.08)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: viewOnly ? '0' : '10px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.78rem', fontWeight: '800', color: C.gold,
          border: `2px solid ${cfg.color}`,
        }}>
          {getInitial(student.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              fontSize: '0.72rem', fontWeight: '700', color: C.muted,
              background: 'rgba(14,34,93,0.06)', borderRadius: '4px', padding: '1px 5px',
            }}>#{index + 1}</span>
            <span style={{ fontSize: '0.83rem', fontWeight: '700', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {student.name}
            </span>
            {isChanged && (
              <span style={{ fontSize: '0.62rem', fontWeight: '700', color: '#92620a', background: C.amberDim, border: `1px solid ${C.amberBorder}`, borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                changed
              </span>
            )}
          </div>
          {student.mobileNumber && (
            <span style={{ fontSize: '0.7rem', color: C.sub, display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}>
              <i className="pi pi-phone" style={{ fontSize: '0.62rem' }} />
              {student.mobileNumber}
            </span>
          )}
        </div>
        <span style={{
          fontSize: '0.65rem', fontWeight: '700', padding: '3px 9px',
          borderRadius: '20px', background: cfg.dim, color: cfg.color,
          border: `1px solid ${cfg.borderColor}`, flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.6rem' }} />
          {cfg.label}
        </span>
        {(!viewOnly || record.remarks) && (
          <button
            onClick={() => setShowRemarks(r => !r)}
            title={viewOnly ? 'View remarks' : 'Add remarks'}
            style={{
              width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${C.border}`,
              background: record.remarks ? C.goldDim : '#F5F7FA',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}
          >
            <i className="pi pi-comment" style={{ fontSize: '0.68rem', color: record.remarks ? C.gold : C.muted }} />
          </button>
        )}
      </div>
      {!viewOnly && (
        <StatusToggle status={record.status} onChange={s => onStatusChange(student.student_id, s)} />
      )}
      {showRemarks && (
        <div style={{ marginTop: '10px', borderTop: `1px solid ${C.border}`, paddingTop: '10px' }}>
          {viewOnly ? (
            <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, fontStyle: record.remarks ? 'normal' : 'italic' }}>
              {record.remarks || 'No remarks added'}
            </p>
          ) : (
            <>
              <textarea
                placeholder="Add remarks (optional)"
                value={record.remarks || ''}
                onChange={e => onRemarksChange(student.student_id, e.target.value)}
                maxLength={200}
                rows={2}
                style={{
                  width: '100%', resize: 'none', border: `1px solid ${C.border}`,
                  borderRadius: '8px', padding: '8px 10px', fontSize: '0.77rem',
                  color: C.body, fontFamily: 'inherit', outline: 'none',
                  boxSizing: 'border-box', transition: 'border-color 0.15s ease',
                }}
                onFocus={e => (e.target.style.borderColor = C.navy)}
                onBlur={e => (e.target.style.borderColor = C.border)}
              />
              <span style={{ fontSize: '0.67rem', color: C.muted, float: 'right', marginTop: '2px' }}>
                {(record.remarks || '').length}/200
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryBar = ({ records, total }: { records: Record<string, AttendanceRecord>; total: number }) => {
  const counts = { present: 0, absent: 0, late: 0 };
  Object.values(records).forEach(r => { counts[r.status]++; });
  const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map(s => {
        const cfg = STATUS_CONFIG[s];
        return (
          <div key={s} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: cfg.dim, border: `1.5px solid ${cfg.borderColor}`,
            borderRadius: '20px', padding: '5px 12px',
          }}>
            <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.72rem', color: cfg.color }} />
            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: cfg.color }}>{counts[s]}</span>
            <span style={{ fontSize: '0.68rem', color: cfg.color, opacity: 0.75 }}>{cfg.label} ({pct(counts[s])}%)</span>
          </div>
        );
      })}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'rgba(14,34,93,0.06)', border: '1.5px solid rgba(14,34,93,0.18)',
        borderRadius: '20px', padding: '5px 12px', marginLeft: 'auto',
      }}>
        <i className="pi pi-users" style={{ fontSize: '0.72rem', color: C.navy }} />
        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: C.navy }}>{total} Students</span>
      </div>
    </div>
  );
};

const SectionAccent = ({ title }: { title: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
    <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px' }}>{title}</span>
  </div>
);

/* ─── Reports Tab ────────────────────────────────────────────────────── */

type ReportView = 'student_summary' | 'date_wise';
type ReportStudentSort = 'name' | 'pct_asc' | 'pct_desc' | 'absent_desc';

const ReportsTab: React.FC<{
  batches: Batch[];
  batchLoading: boolean;
  instituteName: string;
  branchName: string;
}> = ({ batches, batchLoading, instituteName, branchName }) => {
  const toast = useRef<Toast>(null);

  const [reportBatch, setReportBatch] = useState<Batch | null>(null);
  const [reportView, setReportView] = useState<ReportView>('student_summary');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>(formatDate(new Date()));

  const [studentRows, setStudentRows] = useState<ReportStudentRow[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [sortBy, setSortBy] = useState<ReportStudentSort>('pct_desc');
  const [minPct, setMinPct] = useState<string>('');
  const [maxPct, setMaxPct] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [batchSummaryStats, setBatchSummaryStats] = useState<{ total_students: number; total_classes: number; average_attendance: number } | null>(null);
  const [reportBatchInfo, setReportBatchInfo] = useState<any>(null);

  const [dateWiseRows, setDateWiseRows] = useState<BatchAttendanceSummary[]>([]);
  const [dateWiseLoading, setDateWiseLoading] = useState(false);
  const [expandedDateId, setExpandedDateId] = useState<string | null>(null);
  const [dateDetailData, setDateDetailData] = useState<Record<string, any>>({});
  const [dateDetailLoading, setDateDetailLoading] = useState<string | null>(null);

  const handleReportBatchChange = (batchId: string | null, batchObj?: Batch) => {
  if (!batchId) {
    setReportBatch(null);
    return;
  }
  const found = batchObj || batches.find(b => b.batch_id === batchId) || null;
  setReportBatch(found);
};

  const fetchStudentSummary = async () => {
    if (!reportBatch) return;
    setReportLoading(true);
    setStudentRows([]);
    setBatchSummaryStats(null);
    setReportBatchInfo(null);
    setExpandedStudentId(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (minPct) params.set('min_attendance', minPct);
      if (maxPct) params.set('max_attendance', maxPct);

      const res = await apiClient.get(`/v1/attendance/batch/${reportBatch.batch_id}/students?${params}`);
      if (res.data.success) {
        setStudentRows(res.data.data || []);
        setBatchSummaryStats(res.data.summary || null);
        setReportBatchInfo(res.data.batch_info || null);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to fetch report', life: 4000 });
    } finally { setReportLoading(false); }
  };

  const fetchDateWise = async () => {
    if (!reportBatch) return;
    setDateWiseLoading(true);
    setDateWiseRows([]);
    setExpandedDateId(null);
    setDateDetailData({});
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const res = await apiClient.get(`/v1/attendance/batch/${reportBatch.batch_id}?${params}`);
      if (res.data.success) setDateWiseRows(res.data.data || []);
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to fetch date-wise report', life: 4000 });
    } finally { setDateWiseLoading(false); }
  };

  const fetchDateDetail = async (attendanceId: string, date: string) => {
    if (dateDetailData[attendanceId]) return;
    setDateDetailLoading(attendanceId);
    try {
      const res = await apiClient.get(`/v1/attendance/batch/${reportBatch!.batch_id}/date/${date}`);
      if (res.data.success) setDateDetailData(prev => ({ ...prev, [attendanceId]: res.data.data }));
    } catch { } finally { setDateDetailLoading(null); }
  };

  useEffect(() => {
    if (!reportBatch) return;
    if (reportView === 'student_summary') fetchStudentSummary();
    else fetchDateWise();
  }, [reportBatch, reportView]);

  const filteredSortedRows = React.useMemo(() => {
    let rows = [...studentRows];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => r.student_name.toLowerCase().includes(q) || (r.student_mobile || '').includes(q));
    }
    switch (sortBy) {
      case 'name': rows.sort((a, b) => a.student_name.localeCompare(b.student_name)); break;
      case 'pct_asc': rows.sort((a, b) => a.attendance_percentage - b.attendance_percentage); break;
      case 'pct_desc': rows.sort((a, b) => b.attendance_percentage - a.attendance_percentage); break;
      case 'absent_desc': rows.sort((a, b) => b.absent - a.absent); break;
    }
    return rows;
  }, [studentRows, searchQuery, sortBy]);

  /* ── PDF handlers ─────────────────────────────────────────────── */
  const handlePrintStudentSummary = () => {
    if (!reportBatch || !filteredSortedRows.length) return;
    const html = buildStudentSummaryPdf({
      rows: filteredSortedRows,
      batch: reportBatch,
      summary: batchSummaryStats,
      startDate,
      endDate,
      instituteName,
      branchName,
    });
    openPrintWindow(html, `Attendance Report – ${reportBatch.batch_name}`);
  };

  const handlePrintDateWise = () => {
    if (!reportBatch || !dateWiseRows.length) return;
    const html = buildDateWisePdf({
      rows: dateWiseRows,
      batch: reportBatch,
      startDate,
      endDate,
      instituteName,
      branchName,
    });
    openPrintWindow(html, `Attendance Report – ${reportBatch.batch_name} (Date-wise)`);
  };

  const StatCard = ({ icon, label, value, color, dim, border }: {
    icon: string; label: string; value: string | number; color: string; dim: string; border: string;
  }) => (
    <div style={{ flex: 1, minWidth: '110px', background: dim, border: `1.5px solid ${border}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.72rem', color }} />
        <span style={{ fontSize: '0.68rem', color, fontWeight: '600', opacity: 0.8 }}>{label}</span>
      </div>
      <span style={{ fontSize: '1.15rem', fontWeight: '800', color, lineHeight: 1 }}>{value}</span>
    </div>
  );

  const PctRing = ({ pct }: { pct: number }) => {
    const color = getPctColor(pct);
    const r = 18; const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return (
      <svg width="46" height="46" viewBox="0 0 46 46" style={{ flexShrink: 0 }}>
        <circle cx="23" cy="23" r={r} fill="none" stroke={C.border} strokeWidth="4" />
        <circle cx="23" cy="23" r={r} fill="none" stroke={color} strokeWidth="4" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 23 23)" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
        <text x="23" y="27" textAnchor="middle" fontSize="8" fontWeight="800" fill={color}>{pct}%</text>
      </svg>
    );
  };

  /* ── Print button shared style ────────────────────────────────── */
  const printBtnStyle: React.CSSProperties = {
    padding: '7px 16px', borderRadius: '8px', cursor: 'pointer',
    fontSize: '0.73rem', fontWeight: '700', transition: 'all 0.15s ease',
    border: 'none',
    background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
    color: C.gold, display: 'flex', alignItems: 'center', gap: '5px',
    boxShadow: '0 2px 8px rgba(14,34,93,0.2)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      {/* Batch selector – now using BatchFilterControl */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
        <SectionAccent title="Select Batch" />
        {/* FIX: constrain width so the internal horizontal scroller has a
            real bounding box to measure against (matches StudentsFees.tsx). */}
        <div style={{ width: '100%', minWidth: 0 }}>
          <BatchFilterControl
            selectedBatchId={reportBatch?.batch_id || null}
            onBatchChange={handleReportBatchChange}
          />
        </div>
      </div>

      {!reportBatch ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
          <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <i className="pi pi-chart-bar" style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
          </div>
          <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>Select a Batch</h3>
          <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>Choose a batch above to view its attendance report</p>
        </div>
      ) : (
        <>
          {/* Filters row */}
          <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionAccent title="Filters & View" />
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.68rem', fontWeight: '700', color: C.muted }}>From</label>
                <input type="date" value={startDate} max={endDate || formatDate(new Date())} onChange={e => setStartDate(e.target.value)}
                  style={{ padding: '7px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.8rem', color: C.body, outline: 'none', fontFamily: 'inherit' }}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.68rem', fontWeight: '700', color: C.muted }}>To</label>
                <input type="date" value={endDate} min={startDate} max={formatDate(new Date())} onChange={e => setEndDate(e.target.value)}
                  style={{ padding: '7px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.8rem', color: C.body, outline: 'none', fontFamily: 'inherit' }}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setReportView('student_summary')}
                  style={{
                    padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '700',
                    border: `1.5px solid ${reportView === 'student_summary' ? C.navy : C.border}`,
                    background: reportView === 'student_summary' ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : C.white,
                    color: reportView === 'student_summary' ? C.gold : C.navy,
                  }}
                >
                  By Student
                </button>
                <button
                  onClick={() => setReportView('date_wise')}
                  style={{
                    padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '700',
                    border: `1.5px solid ${reportView === 'date_wise' ? C.navy : C.border}`,
                    background: reportView === 'date_wise' ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : C.white,
                    color: reportView === 'date_wise' ? C.gold : C.navy,
                  }}
                >
                  By Date
                </button>
              </div>
              <button
                onClick={() => reportView === 'student_summary' ? fetchStudentSummary() : fetchDateWise()}
                style={{
                  padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '700',
                  border: 'none', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold,
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                <i className="pi pi-refresh" style={{ fontSize: '0.65rem' }} /> Apply
              </button>
            </div>
          </div>

          {/* ── STUDENT SUMMARY VIEW ───────────────────────────────── */}
          {reportView === 'student_summary' && (
            <>
              {reportLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
                  <div style={{ textAlign: 'center' }}>
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" animationDuration=".8s" />
                    <p style={{ marginTop: '12px', color: C.navy, fontSize: '0.88rem', fontWeight: '500' }}>Loading report…</p>
                  </div>
                </div>
              ) : (
                <>
                  {batchSummaryStats && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <StatCard icon="pi-users" label="Students" value={batchSummaryStats.total_students} color={C.navy} dim="rgba(14,34,93,0.06)" border="rgba(14,34,93,0.18)" />
                      <StatCard icon="pi-calendar" label="Classes" value={batchSummaryStats.total_classes} color={C.blue} dim={C.blueDim} border={C.blueBorder} />
                      <StatCard icon="pi-chart-line" label="Avg %" value={`${batchSummaryStats.average_attendance}%`} color={getPctColor(batchSummaryStats.average_attendance)} dim={getPctDim(batchSummaryStats.average_attendance)} border={getPctBorder(batchSummaryStats.average_attendance)} />
                    </div>
                  )}

                  <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy }}>Students</span>
                        <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, borderRadius: '20px', border: `1.5px solid ${C.gold}` }}>{filteredSortedRows.length}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          placeholder="Search name / phone…"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          style={{ padding: '6px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.75rem', width: '160px', outline: 'none' }}
                        />
                        <select value={sortBy} onChange={e => setSortBy(e.target.value as ReportStudentSort)}
                          style={{ padding: '6px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.75rem', outline: 'none' }}>
                          <option value="pct_desc">% High → Low</option>
                          <option value="pct_asc">% Low → High</option>
                          <option value="name">Name A–Z</option>
                          <option value="absent_desc">Most Absent</option>
                        </select>
                        <button onClick={handlePrintStudentSummary} style={printBtnStyle}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <i className="pi pi-print" style={{ fontSize: '0.65rem' }} /> Print / Save PDF
                        </button>
                      </div>
                    </div>

                    {filteredSortedRows.length === 0 ? (
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted }}>No students found for the selected filters</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {filteredSortedRows.map(row => {
                          const expanded = expandedStudentId === row.student_id;
                          const pctColor = getPctColor(row.attendance_percentage);
                          return (
                            <div key={row.student_id} style={{ border: `1px solid ${expanded ? C.goldBorder : C.border}`, borderRadius: '10px', overflow: 'hidden', boxShadow: expanded ? '0 3px 14px rgba(14,34,93,0.08)' : 'none' }}>
                              <button
                                onClick={() => setExpandedStudentId(expanded ? null : row.student_id)}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: expanded ? C.goldDim : C.white, border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: expanded ? `1px solid ${C.goldBorder}` : 'none' }}
                              >
                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800', color: C.gold, border: `2px solid ${pctColor}` }}>
                                  {getInitial(row.student_name)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '0.83rem', fontWeight: '700', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.student_name}</div>
                                  {row.student_mobile && <span style={{ fontSize: '0.68rem', color: C.sub, display: 'flex', alignItems: 'center', gap: '3px' }}><i className="pi pi-phone" style={{ fontSize: '0.6rem' }} />{row.student_mobile}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                                  {(['present', 'absent', 'late'] as const).map(s => {
                                    const cfg = STATUS_CONFIG[s];
                                    return <span key={s} style={{ fontSize: '0.62rem', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: cfg.dim, color: cfg.color, border: `1px solid ${cfg.borderColor}`, display: 'flex', alignItems: 'center', gap: '3px' }}><i className={`pi ${cfg.icon}`} style={{ fontSize: '0.55rem' }} />{row[s]}</span>;
                                  })}
                                </div>
                                <PctRing pct={row.attendance_percentage} />
                                <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ fontSize: '0.7rem', color: C.muted, flexShrink: 0 }} />
                              </button>

                              {expanded && (
                                <div style={{ padding: '12px 14px' }}>
                                  <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                      <span style={{ fontSize: '0.68rem', color: C.muted }}>{row.attended_classes} of {row.total_classes} classes attended</span>
                                      <span style={{ fontSize: '0.68rem', fontWeight: '700', color: pctColor }}>{row.attendance_percentage}%</span>
                                    </div>
                                    <div style={{ height: '6px', borderRadius: '4px', background: C.border, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', borderRadius: '4px', background: pctColor, width: `${row.attendance_percentage}%` }} />
                                    </div>
                                  </div>
                                  {row.records.length === 0 ? (
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: C.muted, fontStyle: 'italic' }}>No detailed records available</p>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '280px', overflowY: 'auto' }}>
                                      {row.records.map((rec, ri) => {
                                        const statusKey: AttendanceStatus = ['present', 'absent', 'late'].includes(rec.status) ? (rec.status as AttendanceStatus) : 'absent';
                                        const cfg = STATUS_CONFIG[statusKey];
                                        return (
                                          <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', background: cfg.dim, border: `1px solid ${cfg.borderColor}` }}>
                                            <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy, minWidth: '80px' }}>{displayDate(rec.date)}</span>
                                            <span style={{ fontSize: '0.62rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: C.white, color: cfg.color, border: `1px solid ${cfg.borderColor}`, display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                                              <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.55rem' }} />{cfg.label}
                                            </span>
                                            {rec.topic && <span style={{ fontSize: '0.7rem', color: C.sub, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><i className="pi pi-book" style={{ fontSize: '0.6rem', marginRight: '3px' }} />{rec.topic}</span>}
                                            {rec.remarks && <span title={rec.remarks} style={{ fontSize: '0.62rem', color: '#92620a', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '4px', padding: '2px 6px', flexShrink: 0, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><i className="pi pi-comment" style={{ fontSize: '0.55rem', marginRight: '2px' }} />{rec.remarks}</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── DATE-WISE VIEW ────────────────────────────────────── */}
          {reportView === 'date_wise' && (
            <>
              {dateWiseLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
                  <div style={{ textAlign: 'center' }}>
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" animationDuration=".8s" />
                    <p style={{ marginTop: '12px', color: C.navy, fontSize: '0.88rem', fontWeight: '500' }}>Loading sessions…</p>
                  </div>
                </div>
              ) : dateWiseRows.length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
                  <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>No Sessions Found</h3>
                  <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>No attendance sessions recorded for the selected date range</p>
                </div>
              ) : (
                <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
                      <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy }}>Sessions</span>
                      <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, borderRadius: '20px', border: `1.5px solid ${C.gold}` }}>{dateWiseRows.length}</span>
                    </div>
                    <button
                      onClick={handlePrintDateWise}
                      style={printBtnStyle}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <i className="pi pi-print" style={{ fontSize: '0.65rem' }} />
                      Print / Save PDF
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {dateWiseRows.map((session) => {
                      const expanded = expandedDateId === session.attendance_id;
                      const total = session.total_students || 1;
                      const presentPct = Math.round((session.present_count / total) * 100);
                      const detail = dateDetailData[session.attendance_id];

                      return (
                        <div key={session.attendance_id} style={{ border: `1px solid ${expanded ? C.goldBorder : C.border}`, borderRadius: '10px', overflow: 'hidden', boxShadow: expanded ? '0 3px 14px rgba(14,34,93,0.08)' : 'none' }}>
                          <button
                            onClick={async () => {
                              if (expanded) { setExpandedDateId(null); }
                              else { setExpandedDateId(session.attendance_id); await fetchDateDetail(session.attendance_id, session.attendance_date); }
                            }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: expanded ? C.goldDim : C.white, border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: expanded ? `1px solid ${C.goldBorder}` : 'none' }}>
                            <div style={{ background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '8px', padding: '6px 10px', flexShrink: 0, textAlign: 'center', minWidth: '72px' }}>
                              <div style={{ fontSize: '0.6rem', color: 'rgba(251,178,31,0.7)', fontWeight: '600', marginBottom: '1px' }}>
                                {new Date(session.attendance_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                              </div>
                              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: C.gold, lineHeight: 1 }}>
                                {new Date(session.attendance_date).getDate().toString().padStart(2, '0')}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>
                                {session.topic || <em style={{ color: C.muted, fontWeight: '400' }}>No topic recorded</em>}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ flex: 1, height: '4px', borderRadius: '3px', background: C.border, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', background: C.green, width: `${presentPct}%`, borderRadius: '3px' }} />
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: '700', color: C.green, flexShrink: 0 }}>{presentPct}% present</span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                              {[
                                { key: 'present', val: session.present_count, cfg: STATUS_CONFIG.present },
                                { key: 'absent', val: session.absent_count, cfg: STATUS_CONFIG.absent },
                                { key: 'late', val: session.late_count, cfg: STATUS_CONFIG.late },
                              ].map(({ key, val, cfg }) => (
                                <span key={key} style={{ fontSize: '0.62rem', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: cfg.dim, color: cfg.color, border: `1px solid ${cfg.borderColor}`, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.55rem' }} />{val}
                                </span>
                              ))}
                            </div>
                            <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ fontSize: '0.7rem', color: C.muted, flexShrink: 0 }} />
                          </button>

                          {expanded && (
                            <div style={{ padding: '12px 14px' }}>
                              {dateDetailLoading === session.attendance_id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}>
                                  <ProgressSpinner style={{ width: '22px', height: '22px' }} strokeWidth="4" animationDuration=".8s" />
                                  <span style={{ fontSize: '0.78rem', color: C.muted }}>Loading student details…</span>
                                </div>
                              ) : detail ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '300px', overflowY: 'auto' }}>
                                  {detail.attendance_records?.map((rec: any, ri: number) => {
                                    const statusKey: AttendanceStatus = ['present', 'absent', 'late'].includes(rec.status) ? rec.status : 'absent';
                                    const cfg = STATUS_CONFIG[statusKey];
                                    return (
                                      <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', background: cfg.dim, border: `1px solid ${cfg.borderColor}` }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', color: C.gold, border: `1.5px solid ${cfg.color}` }}>
                                          {getInitial(rec.student_name || '?')}
                                        </div>
                                        <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.navy, flex: 1 }}>{rec.student_name || 'Unknown'}</span>
                                        <span style={{ fontSize: '0.6rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: C.white, color: cfg.color, border: `1px solid ${cfg.borderColor}`, display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                                          <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.55rem' }} />{cfg.label}
                                        </span>
                                        {rec.remarks && <span title={rec.remarks} style={{ fontSize: '0.6rem', color: '#92620a', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '4px', padding: '2px 6px', flexShrink: 0, maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.remarks}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p style={{ margin: 0, fontSize: '0.78rem', color: C.muted }}>Failed to load details</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const OperatorAttendance: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { profile, loading: profileLoading } = useOperatorProfileStore();
  const { batches, loading: batchLoading, fetchBatches } = useBatchStore();

  const [activeTab, setActiveTab] = useState<ActiveTab>('mark');

  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(new Date()));
  const [students, setStudents] = useState<Student[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, AttendanceRecord>>({});
  const [topic, setTopic] = useState('');
  const [existingId, setExistingId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState<AttendanceStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewOnly, setViewOnly] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<Record<string, AttendanceRecord>>({});
  const [savedTopic, setSavedTopic] = useState('');

  const instituteName = profile?.institute_name ?? 'Hindustan Computer Institute';
  const branchName = profile?.branch_name ?? '';

  useEffect(() => {
    if (profile?.institute_id && !batches) fetchBatches();
  }, [profile]);

  useEffect(() => {
    if (!selectedBatch) return;
    fetchStudents(selectedBatch.batch_id);
    checkExistingAttendance(selectedBatch.batch_id, selectedDate);
  }, [selectedBatch, selectedDate]);

 const handleBatchChange = (batchId: string | null, batchObj?: Batch) => {
  if (!batchId) {
    setSelectedBatch(null);
    return;
  }
  const found = batchObj || (batches || []).find(b => b.batch_id === batchId) || null;
  setSelectedBatch(found as Batch | null);
};

  const fetchStudents = async (batchId: string) => {
    setLoadingStudents(true);
    try {
      const res = await apiClient.get(`/v1/students/get-students-by-batch/${batchId}?status=active&limit=100`);
      if (res.data.success) {
        setStudents(res.data.data);
        const init: Record<string, AttendanceRecord> = {};
        res.data.data.forEach((s: Student) => { init[s.student_id] = { student_id: s.student_id, status: 'present' }; });
        setAttendanceRecords(init);
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load students', life: 3000 });
    } finally { setLoadingStudents(false); }
  };

  const checkExistingAttendance = async (batchId: string, date: string) => {
    setLoadingAttendance(true);
    setIsEditMode(false);
    setExistingId(null);
    setTopic('');
    setSavedTopic('');
    setSavedSnapshot({});
    setViewOnly(false);
    try {
      const res = await apiClient.get(`/v1/attendance/batch/${batchId}/date/${date}`);
      if (res.data.success && res.data.data) {
        const d: ExistingAttendance = res.data.data;
        setExistingId(d.attendance_id);
        setTopic(d.topic || '');
        setSavedTopic(d.topic || '');
        setIsEditMode(true);
        setViewOnly(true);
        const rec: Record<string, AttendanceRecord> = {};
        d.attendance_records.forEach(r => {
          const validStatus: AttendanceStatus = ['present', 'absent', 'late'].includes(r.status) ? (r.status as AttendanceStatus) : 'present';
          rec[r.student_id] = { student_id: r.student_id, status: validStatus, remarks: r.remarks || '' };
        });
        setSavedSnapshot(rec);
        setAttendanceRecords(prev => { const merged = { ...prev }; Object.assign(merged, rec); return merged; });
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to check existing attendance', life: 3000 });
      }
    } finally { setLoadingAttendance(false); }
  };

  const handleStatusChange = useCallback((studentId: string, status: AttendanceStatus) => {
    setAttendanceRecords(prev => ({ ...prev, [studentId]: { ...prev[studentId], status } }));
  }, []);

  const handleRemarksChange = useCallback((studentId: string, remarks: string) => {
    setAttendanceRecords(prev => ({ ...prev, [studentId]: { ...prev[studentId], remarks } }));
  }, []);

  const handleBulkAction = (status: AttendanceStatus) => setShowBulkConfirm(status);

  const confirmBulk = () => {
    if (!showBulkConfirm) return;
    setAttendanceRecords(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(id => { updated[id] = { ...updated[id], status: showBulkConfirm }; });
      return updated;
    });
    setShowBulkConfirm(null);
    toast.current?.show({ severity: 'success', summary: 'Done', detail: `All students marked as ${STATUS_CONFIG[showBulkConfirm].label}`, life: 2000 });
  };

  const handleSubmit = async () => {
    if (!topic.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Please enter the topic covered', life: 3000 });
      return;
    }
    if (!selectedBatch || students.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        batch_id: selectedBatch.batch_id,
        attendance_date: selectedDate,
        topic: topic.trim(),
        attendance_records: Object.values(attendanceRecords).map(r => ({
          student_id: r.student_id,
          status: r.status,
          ...(r.remarks ? { remarks: r.remarks } : {}),
        })),
      };
      const res = await apiClient.post('/v1/attendance/mark', payload);
      if (res.data.success) {
        toast.current?.show({ severity: 'success', summary: 'Success', detail: isEditMode ? 'Attendance updated successfully' : 'Attendance submitted successfully', life: 3000 });
        setIsEditMode(true);
        setViewOnly(true);
        setSavedSnapshot({ ...attendanceRecords });
        setSavedTopic(topic.trim());
        if (res.data.data?.attendance_id) setExistingId(res.data.data.attendance_id);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to submit attendance', life: 4000 });
    } finally { setSubmitting(false); }
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.mobileNumber || '').includes(searchQuery)
  );

  const changedCount = Object.keys(attendanceRecords).filter(id => {
    const curr = attendanceRecords[id];
    const snap = savedSnapshot[id];
    if (!snap) return false;
    return curr.status !== snap.status || (curr.remarks || '') !== (snap.remarks || '');
  }).length + (topic.trim() !== savedTopic.trim() ? 1 : 0);

  if (profileLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
      <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
      <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile…</p>
    </div>
  );

  if (!profile?.institute_id) return (
    <div style={{ padding: '20px' }}>
      <Message severity="warn" text="Unable to load profile data. Please refresh the page." style={{ fontSize: '0.88rem' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      {/* Tab switcher */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '6px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', gap: '4px' }}>
        {([
          { key: 'mark', icon: 'pi-check-square', label: 'Mark Attendance' },
          { key: 'reports', icon: 'pi-chart-bar', label: 'Reports' },
        ] as const).map(tab => {
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '700', transition: 'all 0.2s ease', border: 'none', background: active ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : 'transparent', color: active ? C.gold : C.sub, boxShadow: active ? '0 2px 10px rgba(14,34,93,0.22)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
              <i className={`pi ${tab.icon}`} style={{ fontSize: '0.8rem' }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Mark Attendance Tab ──────────────────────────────────────── */}
      {activeTab === 'mark' && (
        <>
          <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionAccent title="Select Batch & Date" />
            {/*
              Stacked layout: Attendance Date full-width on its own row,
              Select Batch full-width below it. Each row gets `width: 100%`
              (and `minWidth: 0` on the batch row, for the same flexbox
              shrink-to-fit reason described below) so BatchFilterControl's
              internal horizontal scroller always has the full container
              width to measure against — no side-by-side squeeze.
            */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%' }}>
                <label style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy }}>
                  <i className="pi pi-calendar" style={{ marginRight: '4px', fontSize: '0.68rem' }} />Attendance Date
                </label>
                <input type="date" value={selectedDate} max={formatDate(new Date())} onChange={e => setSelectedDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.82rem', color: C.body, outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
              {/*
                FIX (see earlier note): a flex/block child's default
                min-width is `auto`, so it refuses to shrink below the
                intrinsic width of its content (the un-wrapped row of batch
                cards inside BatchFilterControl). `width: 100%` + `minWidth: 0`
                here gives it a real, stable bounding box to measure against,
                so BatchFilterControl's own internal horizontal scroller
                (overflow-x + arrows) works correctly — matching how it
                behaves in StudentsFees.tsx and the ReportsTab below.
              */}
              <div style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy }}>
                  <i className="pi pi-users" style={{ marginRight: '4px', fontSize: '0.68rem' }} />Select Batch
                </label>
                <div style={{ width: '100%', minWidth: 0 }}>
                  <BatchFilterControl
                    selectedBatchId={selectedBatch?.batch_id || null}
                    onBatchChange={handleBatchChange}
                  />
                </div>
              </div>
            </div>

            {selectedBatch && (
              <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '9px', background: 'linear-gradient(135deg, rgba(14,34,93,0.04) 0%, rgba(251,178,31,0.06) 100%)', border: `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: selectedBatch.is_active ? C.green : C.red }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{selectedBatch.batch_name}</span>
                </div>
                <span style={{ fontSize: '0.72rem', color: C.sub, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} />{selectedBatch.batch_time}
                </span>
                <span style={{ fontSize: '0.72rem', color: C.sub, marginLeft: 'auto' }}>{displayDate(selectedDate)}</span>
                {isEditMode && !viewOnly && (
                  <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px', background: C.amberDim, color: '#92620a', border: `1px solid ${C.amberBorder}`, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <i className="pi pi-pencil" style={{ fontSize: '0.6rem' }} />Editing
                  </span>
                )}
              </div>
            )}
          </div>

          {!selectedBatch ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
              <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <i className="pi pi-id-card" style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
              </div>
              <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>Select a Batch</h3>
              <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>Choose a batch above to start marking attendance</p>
            </div>
          ) : loadingStudents || loadingAttendance ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '260px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
              <div style={{ textAlign: 'center' }}>
                <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
                <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>{loadingStudents ? 'Loading students…' : 'Checking attendance…'}</p>
              </div>
            </div>
          ) : students.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
              <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>No Active Students</h3>
              <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>This batch has no active students enrolled</p>
            </div>
          ) : (
            <>
              {isEditMode && viewOnly && (
                <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(16,185,129,0.12) 100%)', border: `1.5px solid ${C.greenBorder}`, borderRadius: '14px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0, background: C.greenDim, border: `1.5px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-check-circle" style={{ fontSize: '1.1rem', color: C.green }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: C.green, marginBottom: '2px' }}>Attendance already marked</div>
                    <div style={{ fontSize: '0.72rem', color: C.sub }}>{displayDate(selectedDate)} · {students.length} students · Topic: <em>{savedTopic || '—'}</em></div>
                  </div>
                  <button onClick={() => setViewOnly(false)}
                    style={{ padding: '8px 16px', borderRadius: '9px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: '700', border: `1.5px solid ${C.navy}`, background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, boxShadow: '0 2px 8px rgba(14,34,93,0.2)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <i className="pi pi-pencil" style={{ fontSize: '0.68rem' }} />Edit Attendance
                  </button>
                </div>
              )}

              {isEditMode && !viewOnly && (
                <div style={{ background: C.amberDim, border: `1.5px solid ${C.amberBorder}`, borderRadius: '14px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '1rem', color: '#92620a', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', color: '#92620a', flex: 1 }}>
                    {changedCount > 0
                      ? <><strong>{changedCount} change{changedCount > 1 ? 's' : ''}</strong> from the last saved record</>
                      : 'No changes yet — edit statuses or topic below'}
                  </span>
                  <button onClick={() => { setAttendanceRecords({ ...savedSnapshot }); setTopic(savedTopic); setViewOnly(true); }}
                    style={{ padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '700', border: `1px solid ${C.redBorder}`, background: C.redDim, color: C.red, display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />Discard Changes
                  </button>
                </div>
              )}

              <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                <SectionAccent title="Topic Covered *" />
                <textarea placeholder="Enter the topic covered in this session…" value={topic} onChange={e => { if (!viewOnly) setTopic(e.target.value); }} readOnly={viewOnly} maxLength={200} rows={2}
                  style={{ width: '100%', resize: 'none', border: `1.5px solid ${viewOnly ? C.border : topic.trim() ? C.navy : C.border}`, borderRadius: '9px', padding: '10px 12px', fontSize: '0.83rem', color: viewOnly ? C.sub : C.body, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', background: viewOnly ? '#F9FAFB' : C.white, cursor: viewOnly ? 'default' : 'text' }}
                  onFocus={e => { if (!viewOnly) e.target.style.borderColor = C.navy; }}
                  onBlur={e => { if (!viewOnly) e.target.style.borderColor = topic.trim() ? C.navy : C.border; }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  {!viewOnly && !topic.trim() && <span style={{ fontSize: '0.68rem', color: C.red }}><i className="pi pi-exclamation-circle" style={{ marginRight: '3px', fontSize: '0.65rem' }} />Required before submitting</span>}
                  {!viewOnly && <span style={{ fontSize: '0.67rem', color: C.muted, marginLeft: 'auto' }}>{topic.length}/200</span>}
                </div>
              </div>

              {!viewOnly && (
                <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                  <SectionAccent title="Quick Actions" />
                  <div style={{ marginBottom: '12px' }}><SummaryBar records={attendanceRecords} total={students.length} /></div>
                  <div style={{ height: '6px', borderRadius: '4px', background: C.border, overflow: 'hidden', marginBottom: '14px' }}>
                    {(() => {
                      const counts = { present: 0, absent: 0, late: 0 };
                      Object.values(attendanceRecords).forEach(r => { counts[r.status]++; });
                      const t = students.length || 1;
                      return (
                        <div style={{ display: 'flex', height: '100%' }}>
                          <div style={{ width: `${(counts.present / t) * 100}%`, background: C.green }} />
                          <div style={{ width: `${(counts.late / t) * 100}%`, background: C.amber }} />
                          <div style={{ width: `${(counts.absent / t) * 100}%`, background: C.red }} />
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', color: C.muted, alignSelf: 'center', marginRight: '2px' }}>Mark all as:</span>
                    {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map(s => {
                      const cfg = STATUS_CONFIG[s];
                      return (
                        <button key={s} onClick={() => handleBulkAction(s)}
                          style={{ padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '700', border: `1.5px solid ${cfg.borderColor}`, background: cfg.dim, color: cfg.color, display: 'flex', alignItems: 'center', gap: '5px' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                          <i className={`pi ${cfg.icon}`} style={{ fontSize: '0.65rem' }} />{cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy }}>Students</span>
                    <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, borderRadius: '20px', border: `1.5px solid ${C.gold}` }}>{filteredStudents.length}</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <i className="pi pi-search" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.68rem', color: C.muted }} />
                    <input placeholder="Search by name or phone…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      style={{ paddingLeft: '28px', paddingRight: '10px', paddingTop: '6px', paddingBottom: '6px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.78rem', color: C.body, outline: 'none', fontFamily: 'inherit', width: '200px' }}
                      onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 0 }}>
                        <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                      </button>
                    )}
                  </div>
                </div>

                {filteredStudents.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted }}>No students match your search</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredStudents.map((student, i) => (
                      <StudentRow
                        key={student.student_id}
                        student={student}
                        record={attendanceRecords[student.student_id] || { student_id: student.student_id, status: 'present' }}
                        onStatusChange={handleStatusChange}
                        onRemarksChange={handleRemarksChange}
                        index={i}
                        viewOnly={viewOnly}
                        isChanged={!viewOnly && !!savedSnapshot[student.student_id] && (
                          attendanceRecords[student.student_id]?.status !== savedSnapshot[student.student_id]?.status ||
                          (attendanceRecords[student.student_id]?.remarks || '') !== (savedSnapshot[student.student_id]?.remarks || '')
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>

              {!viewOnly && (
                <div style={{ position: 'sticky', bottom: '16px', background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 -2px 16px rgba(14,34,93,0.1)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: C.navy }}>{isEditMode ? 'Update Attendance' : 'Submit Attendance'}</div>
                    <div style={{ fontSize: '0.68rem', color: C.muted, marginTop: '2px' }}>
                      {selectedBatch.batch_name} · {displayDate(selectedDate)} · {students.length} students
                      {isEditMode && changedCount > 0 && <span style={{ color: '#92620a', marginLeft: '6px', fontWeight: '600' }}>· {changedCount} change{changedCount > 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <button onClick={handleSubmit} disabled={submitting || !topic.trim()}
                    style={{ padding: '10px 24px', borderRadius: '10px', cursor: submitting || !topic.trim() ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: '700', border: 'none', opacity: submitting || !topic.trim() ? 0.6 : 1, background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, boxShadow: '0 3px 10px rgba(14,34,93,0.25)', display: 'flex', alignItems: 'center', gap: '7px' }}
                    onMouseEnter={e => { if (!submitting && topic.trim()) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {submitting
                      ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.78rem' }} /> Submitting…</>
                      : <><i className="pi pi-check-circle" style={{ fontSize: '0.78rem' }} />{isEditMode ? 'Update' : 'Submit'} Attendance</>}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <ReportsTab
          batches={batches || []}
          batchLoading={batchLoading}
          instituteName={instituteName}
          branchName={branchName}
        />
      )}

      <Dialog
        visible={!!showBulkConfirm}
        onHide={() => setShowBulkConfirm(null)}
        header={<span style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '700' }}>Confirm Bulk Action</span>}
        style={{ width: '340px', borderRadius: '14px' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '4px 0' }}>
            <button onClick={() => setShowBulkConfirm(null)} style={{ padding: '7px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
            <button onClick={confirmBulk} style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', color: C.gold, fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}>Confirm</button>
          </div>
        }
      >
        {showBulkConfirm && (
          <p style={{ margin: 0, fontSize: '0.83rem', color: C.body, lineHeight: '1.5' }}>
            This will mark all <strong>{students.length} students</strong> as{' '}
            <strong style={{ color: STATUS_CONFIG[showBulkConfirm].color }}>{STATUS_CONFIG[showBulkConfirm].label}</strong>. Individual statuses will be overwritten.
          </p>
        )}
      </Dialog>
    </div>
  );
};

export default OperatorAttendance;